import { LitElement } from 'lit'

import { routes } from './routes.js'
import AppEvents from '../app-events.js'
import { appendParamsObjToPath, decodeQuerystringValues, getRouteByPath } from './router-util.js'
import { getSessionUser, initializeSessionFromJWT } from '../session/session.js'
import { assert } from '../shared/assert.js'
import permissionTypes from '../permissions/permissions.js'
import { toast } from '../shared/toast.js'

/**
 * @param {string} path
 * @param {Object<string, *>} params
 * */
export function go(path, params = null) {
  if (getRouteByPath(path, routes)) {
    window.history.pushState(null, null, params ? appendParamsObjToPath(path, params) : path)
    window.dispatchEvent(new CustomEvent(AppEvents.ROUTE_CHANGE))
  }
}

/** 
 * @param {SessionUser} sessionUser 
 * @returns {Route|null} - route object if user is authenticated and has a default route
 * */
export function getUserDefaultRoute(sessionUser) {
  if (sessionUser?.userId) {
    if (sessionUser.kind === permissionTypes.ADMIN || sessionUser.kind === permissionTypes.SUPERUSER) {
      return routes.ADMIN
    }
    if (sessionUser.kind === permissionTypes.CLIENT) {
      go(routes.DASHBOARD.path)
      return routes.DASHBOARD
    }
  }

  return null
}

/** @param {Object<string, *>} params */
export function updateUrlParams(params) {
  assert(typeof params === 'object', 'params must be an object when updating URL params')
  window.history.pushState(
    null,
    null,
    params ? appendParamsObjToPath(window.location.pathname, params) : window.location.pathname
  )
}

/** @param {string[]} paramsToRemove */
export function removeUrlParams(paramsToRemove = []) {
    assert(Array.isArray(paramsToRemove), 'paramsToRemove must be an array')
    const currentUrl = new URL(window.location.href)
    const currentParams = new URLSearchParams(currentUrl.search)

    paramsToRemove.forEach(p => {
      currentParams.delete(p) // Remove noteId if present
    })

    updateUrlParams(currentParams)
}


/**
 * @param {Route} routeObj
 * @returns {Route | null}
 * */
function checkRoutePermissions(routeObj) {
  /** @type {PermissionParams} */
  const options = {
    sessionUser: getSessionUser(),
  }

  let redirect = null

  // @ts-ignore
  routeObj.permissions.forEach((p) => {
    const result = p(options)
    if (result) {
      redirect = result
    }
  })

  return redirect
}

const componentLoader = (/** @type {string} */ pathname) => import(`./../views/${pathname}.js`)

/**
 * @param {typeof LitElement} SuperClass
 */
export default (SuperClass) => {
  return class extends SuperClass {
    static properties = {
      showNav: { type: Boolean },
      showHeader: { type: Boolean },
      showBackArrow: { type: Boolean },
      backArrowLocation: { type: String },
      backArrowOverride: { type: String },
      showHeaderQuickNav: { type: Boolean },
      history: { type: Array },
    }
    constructor() {
      super()

      // Handle back and forward button events
      window.addEventListener('popstate', () => this.urlChange(), false)

      // Handle the initial route
      window.addEventListener('load', () => this.urlChange(), false)

      /** @type {*} */
      this.history = []

      // Start session initialization (don't await - track with promise)
      this.sessionInitialized = this.initializeSession()

      // Check for deep links from service worker
      this.checkForDeepLink()
    }
    
    async initializeSession() {
      try {
        const restored = await initializeSessionFromJWT()
        console.log('[1] 🔗 ROUTER: Session initialization complete:', restored ? 'restored' : 'not found')
        return restored
      } catch (err) {
        console.error('🔗 ROUTER: Error initializing session:', err)
        return false
      }
    }
    
    /**
     * Send the app to a destination the service worker recorded for us.
     * Ignores anything malformed or already-current so a repeated check is a
     * no-op rather than a redundant navigation.
     *
     * @param {{pathname?: string, search?: string}} deepLink
     * @returns {boolean} whether a navigation was performed
     */
    applyDeepLink(deepLink) {
      if (!deepLink || typeof deepLink.pathname !== 'string') return false

      const targetPath = deepLink.pathname + (deepLink.search || '')
      if (window.location.pathname + window.location.search === targetPath) return false

      window.history.replaceState(null, null, targetPath)
      // Small delay to ensure everything is initialized
      setTimeout(() => this.urlChange(), 100)
      return true
    }

    /**
     * Read a destination the service worker persisted to the Cache API.
     *
     * This is the reliable half of deep-link recovery: the cache is durable, so
     * it survives the worker being terminated between the notification click
     * and the app's cold boot, and the page can read it directly — no
     * postMessage round-trip, so nothing races the worker's lifecycle.
     *
     * Always consumed (deleted) on read, whether or not it is still fresh, so a
     * stale entry can never hijack a later ordinary launch.
     */
    async readPersistedDeepLink() {
      if (!('caches' in window)) return null

      try {
        const cache = await caches.open('ejm-pending-deep-link')
        const response = await cache.match('/__pending-deep-link')
        if (!response) return null

        await cache.delete('/__pending-deep-link')

        const entry = await response.json()
        // Generous window: a cold PWA launch is slow, but this still expires
        // rather than resurfacing hours later on an unrelated app open.
        if (!entry || Date.now() - entry.timestamp > 60000) return null
        return entry
      } catch (error) {
        console.error('🔗 ROUTER: Error reading persisted deep link:', error)
        return null
      }
    }

    async checkForDeepLink() {
      // Durable path first — see readPersistedDeepLink.
      const persisted = await this.readPersistedDeepLink()
      if (persisted && this.applyDeepLink(persisted)) return

      // Wait for service worker to be ready
      if (!('serviceWorker' in navigator)) {
        return
      }

      /** @type {(event: MessageEvent) => void} */
      let messageHandler = null

      try {
        const registration = await navigator.serviceWorker.ready
        if (!registration.active) {
          return
        }

        // Set up message listener first
        const messagePromise = new Promise((resolve) => {
          const timeout = setTimeout(() => {
            resolve(null)
          }, 2000)

          messageHandler = (event) => {
            if (event.data.type === 'DEEP_LINK_AVAILABLE') {
              clearTimeout(timeout)
              resolve(event.data.data)
            } else if (event.data.type === 'NO_DEEP_LINK') {
              clearTimeout(timeout)
              resolve(null)
            }
            // NOTE: PUSH_RECEIVED deliberately does not resolve this. Its
            // payload is a push message ({title, body, data}), not a deep link,
            // so treating it as one produced a literal "undefinedundefined" URL.
          }

          navigator.serviceWorker.addEventListener('message', messageHandler)
        })

        // Request deep link from SW
        registration.active.postMessage({ type: 'CHECK_DEEP_LINK' })

        // Wait for response
        const deepLink = await messagePromise

        this.applyDeepLink(deepLink)
      } catch (error) {
        console.error('🔗 ROUTER: Error checking for deep link:', error)
      } finally {
        // This runs on every app resume, so a listener left behind each time
        // would accumulate for the life of the session.
        if (messageHandler) navigator.serviceWorker.removeEventListener('message', messageHandler)
      }
    }

    connectedCallback() {
      super.connectedCallback()

      window.addEventListener(AppEvents.ROUTE_CHANGE, () => this.urlChange(), false)
    }

    async urlChange() {
      if (this.backArrowOverride) this.backArrowOverride = null
      const { pathname, search } = window.location

      // Wait for session initialization before making any auth-gated routing
      // decision, not just on the root path. getSessionUser() reads
      // sessionStorage synchronously, which is empty on every cold app
      // launch — the actual JWT lives in an httpOnly cookie and is only
      // restored into sessionStorage once initializeSessionFromJWT()'s
      // network round-trip resolves. A cold launch straight into a deep
      // link (push notification, shared URL) previously raced ahead of that
      // restore and incorrectly bounced an authenticated user to /login.
      if (this.sessionInitialized) {
        await this.sessionInitialized
      }

      const sessionUser = getSessionUser()

      // handle redirects base on session user
      if (pathname === '/') {
        const defaultRoute = getUserDefaultRoute(sessionUser)
        if (defaultRoute) {
          return go(defaultRoute.path)
        }
        return go(routes.MAP.path)
      }

      const routeObj = getRouteByPath(pathname, routes)
      console.log('🚦 ROUTER: Route found:', routeObj?.id || 'NO ROUTE')
      
      const permissionRedirectRoute = checkRoutePermissions(routeObj)
      if (!routeObj || permissionRedirectRoute) {
        return go(permissionRedirectRoute.path)
      }

      this.showNav = routeObj?.showNav && !!sessionUser?.userId
      this.showHeader = routeObj?.showHeader == true
      this.showBackArrow = routeObj?.showBackArrow == true
      this.showHeaderQuickNav = routeObj?.showHeaderQuickNav
      this.backArrowLocation = routeObj?.backArrowLocation

      try {
        // load and cache component route file. Works with webpack dynamic imports for on demand loading and caching
        await componentLoader(routeObj.componentPath)
      } catch (err) {
        assert(false, `route attempted to load component that does not exist, Error: ${JSON.stringify(err)}`)
        toast('This view is not available at this time.', 'error')
        go(this.history[this.history.length - 1].path)

        return
      }

      /** @type {RouteContext} */
      const context = { params: decodeQuerystringValues(search) }
      this.history = [...this.history, { ...routeObj, context }]
      this.history.length > 30 && (this.history.length = 30)

      this.updateUI(routeObj, context)
    }

    /** 
     * @param {Route} routeObj 
     * @returns {RouteContainerComponent}
     */
    createRouteContainer(routeObj) {
      return /** @type {RouteContainerComponent} */ (document.createElement(routeObj.componentName))
    }

    /**
     * @param {Route} nextView
     * @param {RouteContext} context
     * */
    async updateUI(nextView, context) {
      assert(!!nextView?.componentName, `malformed route. Route: ${JSON.stringify(nextView)}`)
      const component = this.createRouteContainer(nextView)
      try {
        // @ts-ignore
        if (component.routeEnter) {
          // if routeEnter returns a route for a redirect go there instead of updating UI right away
          // this prevents a flash of content
          const resultRoute = await component.routeEnter({ nextView, context })
          if (resultRoute) {
            go(resultRoute.path)
            return
          }
        }
      } catch (error) {
        console.error('🚦 ROUTER: Error in component routeEnter:', error)
      }
      
      if (component.onGlobalsUpdate) {
        component.disconnectGlobals = window.Globals.onUpdate(component?.onGlobalsUpdate?.bind(component))
      }

      this.$slot().innerHTML = ''
      this.$slot().append(component)
    }

    /** @returns {HTMLSlotElement} */
    $slot() {
      return this.shadowRoot.querySelector('slot')
    }
  }

}

  /**
   * Chrome fires popstate on load, unlike Firefox and Safari which do not
   * This prevents that behavior and makes Chrome function like FF and S
   */
  ; (function() {
    let blockPopstateEvent = document.readyState != 'complete'
    window.addEventListener(
      'load',
      () =>
        // The timeout ensures that popstate-events will be unblocked right
        // after the load event occured, but not in the same event-loop cycle.
        setTimeout(function() {
          blockPopstateEvent = false
        }, 0),
      false
    )
    window.addEventListener(
      'popstate',
      (evt) => {
        if (blockPopstateEvent && document.readyState == 'complete') {
          evt.preventDefault()
          evt.stopImmediatePropagation()
        }
      },
      false
    )
  })()
